import { Link } from 'react-router-dom';
import { IFarmResource } from '../../../entities/farms.entities';
import NameWithPercent from '../NameWithPercent';
import Tooltip from '../Tooltip';
import { Image, Table } from 'antd';
import farm_image from '../../../images/farm.png';
import useMenuHandler from '../../../lib/useMenuHandler';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAccount,
  selectIsAdmin,
  selectProfile,
} from '../../../store/auth/auth.selector';
import { sendSingleRequest } from '../../../apis';
import { loadFarmsData } from '../../../store/farms/farms.actions';
import { showFeedback } from '../../../store/ui/ui.actions';
import LinesTable from './LinesTable';
import CaretDownIcon from '../icons/CaretDownIcon';
import { selectLang } from '../../../store/ui/ui.selector';
import { componentTranslate, translate } from '../../../lib/lang.helper';
import ModalComponent from '../modal/Modal';
import DotsMenu, { IMenu } from '../dropdown-menu/DotsMenu';
import FarmStatusButton from '../../farm-util/FarmStatusButton';
import { ForecastCell } from '../../CommonViews';
import { CROP_COLORS } from '../../../lib/constant';
import { displayValueUnit } from '../../../lib/common.helpers';

interface Props {
  farmsData: IFarmResource[];
  isActive: boolean;
}

const FarmsTable = ({ farmsData, isActive }: Props) => {
  const dispatch = useDispatch<any>();
  const { redirectToFarmVisualLine, redirectToEditFarm, redirectToFarm } =
    useMenuHandler();
  const profile = useSelector(selectProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [visFarm, setVisFarm] = useState<IFarmResource>();
  const [forecasts, setForecasts] = useState<any>([]);
  const [farmToDelete, setFarmToDelete] = useState<IFarmResource>();

  const archiveFarm = async (farm: IFarmResource) => {
    if (
      !window.confirm(
        translate(lang, 'Are you sure you want to archive this farm?'),
      )
    )
      return;

    const response = await sendSingleRequest(
      {},
      'POST',
      `api/farm/archive-farm/${farm.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: 'Farm archived successfully',
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, response.data?.message ?? 'Unknown error'),
        }),
      );
    }
  };
  const confirmDeleteFarm = async () => {
    if (!farmToDelete) return;
    const response = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/farms/${farmToDelete.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: 'Farm deleted successfully',
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, response.data?.message ?? 'Unknown error'),
        }),
      );
    }
    setFarmToDelete(undefined);
  };

  const columns = useMemo(() => {
    let res = [
      {
        title: translate(lang, 'Farm'),
        key: 'farm',
        className: 'drag-farm',
        render: (farm: IFarmResource) => (
          <div className={farm?.is_flupsy ? 'flupsy-farm-title' : ''}>
            <p>{farm.name}</p>
            <div>
              <span className='pr-6'>{farm.farm_number}</span>
            </div>
          </div>
        ),
      },
      {
        title: translate(lang, 'Crops'),
        key: 'crops',
        render: (farm: IFarmResource) => {
          const hasMussel = farm.lines.some(
            line => line.crop_type === 'MUSSEL',
          );
          const hasOyster = farm.lines.some(
            line => line.crop_type === 'OYSTER',
          );
          const hasSeaweed = farm.lines.some(
            line => line.crop_type === 'SEAWEED',
          );
          return (
            <div>
              {hasMussel && (
                <div style={{ color: CROP_COLORS.MUSSEL }}>
                  {translate(lang, 'Mussel')}
                </div>
              )}
              {hasOyster && (
                <div style={{ color: CROP_COLORS.OYSTER }}>
                  {translate(lang, 'Oyster')}
                </div>
              )}
              {hasSeaweed && (
                <div style={{ color: CROP_COLORS.SEAWEED }}>
                  {translate(lang, 'Seaweed')}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: translate(lang, 'Growing Area'),
        key: 'growing_area',
        className: 'drag-area',
        render: (farm: IFarmResource) =>
          !farm.growing_area ? <></> : <span>{farm.growing_area}</span>,
      },
      {
        title: translate(lang, 'Area'),
        key: 'area',
        className: 'drag-area',
        render: (farm: IFarmResource) =>
          displayValueUnit(farm.area, unitType, 'area_hectare'),
      },
      {
        title: `${translate(lang, 'Owner')} / ${translate(
          lang,
          'Area Closing',
        )}`,
        key: 'owners',
        render: (farm: IFarmResource) => {
          const tooltipItems = (farm.owners ?? []).map((owner, i) => ({
            title: `${owner?.title} - ${owner?.percent}%`,
            id: i,
          }));
          return (
            <>
              <Tooltip content={tooltipItems} position='bottom'>
                <div className='tooltip d-flex'>
                  {(farm.owners ?? []).map(
                    ({ title, percent }: any, i: number) => (
                      <div key={i} className='table__owner'>
                        <NameWithPercent
                          title={title}
                          percent={percent}
                          index={i + 1}
                        />
                      </div>
                    ),
                  )}
                </div>
              </Tooltip>
              {!farm.account_id && <FarmStatusButton farm={farm} />}
            </>
          );
        },
      },
      {
        title: translate(lang, 'Cycle'),
        key: 'cycle',
        render: (farm: IFarmResource) => (
          <ForecastCell farm={farm} className='wp-190' lineProps={forecasts} />
        ),
      },
      {
        title: '',
        key: 'visual',
        render: (f: IFarmResource) => (
          <Link
            to={`/farms/visual-lines/${f.id}`}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              redirectToFarmVisualLine(f.id);
            }}
          >
            <Image src={farm_image} style={{ width: '25px' }} />
          </Link>
        ),
      },
    ];
    res.push({
      title: '',
      key: 'more',
      render: (d: IFarmResource) => {
        let menuItems: IMenu[] = [
          {
            label: translate(lang, 'Edit'),
            onClick: () => redirectToEditFarm(d.id.toString()),
          },
        ];
        if (isAdmin) {
          if (d.is_active) {
            menuItems = [
              ...menuItems,
              {
                label: translate(lang, 'Archive'),
                onClick: () => archiveFarm(d),
              },
            ];
          }
          menuItems = [
            ...menuItems,
            {
              label: translate(lang, 'Delete'),
              onClick: () => setFarmToDelete(d),
              disabled: !isAdmin,
            },
          ];
        }
        menuItems = [
          ...menuItems,
          {
            label: translate(lang, 'Share'),
            onClick: () => setVisFarm(d),
          },
        ];
        return (
          <div>
            {!d.account_id && !!profile?.edit_permission && (
              <DotsMenu items={menuItems} />
            )}
          </div>
        );
      },
    });
    return res;
  }, [redirectToFarmVisualLine, profile, lang]);

  useEffect(() => {
    (async () => {
      const res = await sendSingleRequest(
        {},
        'GET',
        'api/farm/lines-props',
        true,
      );
      if (res.status) {
        setForecasts(res.data);
      }
    })();
  }, []);

  return (
    <>
      <Table
        className={'table table--isFarms'}
        rowKey={'id'}
        pagination={false}
        columns={columns}
        dataSource={farmsData}
        onRow={dataRow => ({
          onClick: () => isActive && redirectToFarm(dataRow.id.toString()),
        })}
        expandable={{
          expandedRowRender: d => (
            <LinesTable farmData={d} isChild={true} isActive={isActive} />
          ),
          expandIcon: ({ onExpand, record }) => (
            <div
              className='pt-20 pb-20'
              onKeyDown={() => undefined}
              onClick={event => {
                event.stopPropagation();
                onExpand(record, event);
              }}
            >
              <CaretDownIcon />
            </div>
          ),
        }}
      />
      {visFarm && (
        <ModalComponent
          visible={true}
          onCancel={() => setVisFarm(undefined)}
          type='success'
          title={translate(lang, '_share_farm_another_account')}
          text={componentTranslate(
            lang,
            'Your farm ID is %s. Please make sure you share it with trusted person as they will gain access to your farm data.',
            <strong key={1}>{visFarm.id}</strong>,
          )}
          hideCancelBtn={true}
          onConfirm={() => setVisFarm(undefined)}
        />
      )}
      {farmToDelete && (
        <ModalComponent
          visible={true}
          onCancel={() => setFarmToDelete(undefined)}
          type='warning'
          title={translate(lang, 'Delete Farm')}
          text={
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '2px solid #ff4d4f',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#d9363e',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ⚠️ {translate(lang, 'WARNING: PERMANENT ACTION')}
                </div>
                <div style={{ fontSize: '15px', lineHeight: '1.6' }}>
                  {componentTranslate(
                    lang,
                    'You are about to permanently delete the farm:',
                  )}
                </div>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#d9363e',
                    margin: '12px 0',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderLeft: '4px solid #ff4d4f',
                  }}
                >
                  {farmToDelete.name}
                </div>
              </div>
              <div style={{ fontSize: '14px', marginBottom: '12px' }}>
                <strong>
                  {translate(lang, 'This will permanently remove:')}
                </strong>
              </div>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>{translate(lang, 'All farm lines and associated data')}</li>
                <li>{translate(lang, 'All crops and growing records')}</li>
                <li>{translate(lang, 'All tasks and automation settings')}</li>
                <li>{translate(lang, 'All historical data and reports')}</li>
              </ul>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: '#d9363e',
                  marginTop: '16px',
                }}
              >
                {translate(
                  lang,
                  'This action cannot be undone. Are you absolutely sure?',
                )}
              </div>
            </div>
          }
          onConfirm={confirmDeleteFarm}
        />
      )}
    </>
  );
};

export default FarmsTable;
